import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { FunnelIcon } from '@heroicons/react/20/solid';
import { PickerOption, PropertyPicker, chipClass } from '../violation/linear/PropertyPicker';

/**
 * Linear's filter bar, generalised (#603 follow-on 3): one chip per field,
 * reading as a sentence ("Status is any of Occupied, Vacant"), × to clear,
 * unset fields as quiet "+ Status" buttons. Fields AND together; values
 * within a field OR. The violations list keeps its own tuned copy.
 */
export interface FilterField {
  key: string;
  label: string;
  options: PickerOption[];
  values: string[];
}

interface Props {
  fields: FilterField[];
  onFieldChange: (key: string, values: string[]) => void;
  search: string;
  onSearch: (value: string) => void;
  searchLabel: string;
  shown: number;
  total: number;
  /** At the far right, after search: the list's create buttons. */
  trailing?: React.ReactNode;
}

const summarise = (values: string[], options: PickerOption[]) =>
  values.length <= 2
    ? values.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ')
    : `${values.length} selected`;

/** Picker in a stable child slot, so choosing a value doesn't remount it. */
const Field: React.FC<{ field: FilterField; onChange: (values: string[]) => void }> = ({
  field,
  onChange,
}) => {
  const active = field.values.length > 0;
  return (
    <span
      className={clsx(
        'inline-flex items-center text-[13px]',
        active && 'rounded-md border border-line'
      )}
    >
      {active && <span className="px-2 py-1 text-fg-muted">{field.label}</span>}
      {active && (
        <span className="border-l border-line px-1 text-fg-muted">
          {field.values.length > 1 ? 'is any of' : 'is'}
        </span>
      )}
      <span className={clsx(active && 'border-l border-line')}>
        <PropertyPicker
          multiple
          options={field.options}
          value={field.values}
          onChange={onChange}
          placeholder={`${field.label}…`}
          ariaLabel={`Filter by ${field.label.toLowerCase()}`}
          trigger={
            active ? (
              <span className="font-medium">{summarise(field.values, field.options)}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-fg-muted">
                <span aria-hidden>+</span> {field.label}
              </span>
            )
          }
        />
      </span>
      {active && (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={`Remove ${field.label} filter`}
          className="border-l border-line px-1.5 py-1 text-fg-muted hover:text-fg"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
};

export const ListFilterBar: React.FC<Props> = ({
  fields,
  onFieldChange,
  search,
  onSearch,
  searchLabel,
  shown,
  total,
  trailing,
}) => {
  const anyActive = fields.some((f) => f.values.length > 0);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line/60 px-3 py-2">
      <FunnelIcon className="h-4 w-4 text-fg-faint" aria-hidden />
      {fields.map((f) => (
        <Field key={f.key} field={f} onChange={(v) => onFieldChange(f.key, v)} />
      ))}
      {anyActive && (
        <button
          type="button"
          className={clsx(chipClass, 'text-fg-muted')}
          onClick={() => fields.forEach((f) => f.values.length && onFieldChange(f.key, []))}
        >
          Clear
        </button>
      )}
      <div className="ml-auto flex items-center gap-3">
        <span className="text-[12px] text-fg-muted" aria-live="polite">
          {shown === total ? `${total}` : `${shown} of ${total}`}
        </span>
        <label className="flex items-center gap-1.5 rounded-md border border-line px-2 py-1 focus-within:ring-2 focus-within:ring-accent">
          <MagnifyingGlassIcon className="h-3.5 w-3.5 text-fg-faint" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search"
            aria-label={searchLabel}
            className="w-40 border-0 bg-transparent p-0 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
          />
        </label>
        {trailing}
      </div>
    </div>
  );
};
